import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Checkbox, Collapse, Input, Space, Tag, Typography, message } from "antd";
import { PlusOutlined, ProfileOutlined } from "@ant-design/icons";
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";

import { analyzeDocuments, convertToPdf, generateDocument, getHealth, readErrorMessage } from "api/builderApi";
import OutlineTree from "./OutlineTree";
import PreviewModal, { GenerateControls } from "./PreviewModal";
import SourcePanel from "./SourcePanel";
import StructurePanel from "./StructurePanel";
import { psmfOutline } from "./psmfOutline";
import * as structure from "./structure";
import styles from "./DocumentBuilder.module.css";

const { Text } = Typography;

const DEFAULT_MAX_DOCUMENTS = 20;

const EMPTY_COVER = {
  documentTitle: "",
  documentType: "",
  currentDataLockPoint: "",
  previousDataLockPoint: "",
  includeToc: true,
  stripSourceNumbering: true
};

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const DocumentBuilder = () => {
  const [health, setHealth] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [sections, setSections] = useState([]);
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [cover, setCover] = useState(EMPTY_COVER);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // The preview is an object URL; the previous one is released whenever it is
  // replaced, and on unmount, so a long session does not accumulate blobs.
  const previewUrlRef = useRef(null);

  const releasePreview = useCallback(() => {
    if (previewUrlRef.current) {
      window.URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  }, []);

  useEffect(() => releasePreview, [releasePreview]);

  useEffect(() => {
    getHealth()
      .then(setHealth)
      .catch(() => setHealth(null));
  }, []);

  const maxDocuments = health?.maxSourceDocuments || DEFAULT_MAX_DOCUMENTS;
  const pdfAvailable = health?.pdfPreview?.available !== false;

  // ----------------------------------------------------------------
  // Source documents
  // ----------------------------------------------------------------

  // Read from a ref so overlapping drops see the latest list, not a stale
  // closure. Ant Design used to fire onChange once per file with a growing
  // list; even after that is fixed, two quick drops must not race.
  const documentsRef = useRef(documents);
  documentsRef.current = documents;

  const handleAddDocuments = useCallback(
    async (files) => {
      const currentDocuments = documentsRef.current;
      const existing = new Set(currentDocuments.map((document) => `${document.filename}:${document.size}`));
      const seen = new Set();
      const additions = [];

      for (const file of files) {
        if (!file?.name || !file.name.toLowerCase().endsWith(".docx")) continue;
        const key = `${file.name}:${file.size}`;
        if (existing.has(key) || seen.has(key)) continue;
        seen.add(key);
        additions.push(file);
      }

      if (!additions.length) return;

      const room = maxDocuments - currentDocuments.length;
      if (room <= 0) {
        message.warning(`At most ${maxDocuments} documents can be loaded at once.`);
        return;
      }

      const accepted = additions.slice(0, room).map((file) => ({
        documentId: structure.newId("doc"),
        filename: file.name,
        size: file.size,
        file
      }));

      setAnalyzing(true);
      try {
        const outlines = await analyzeDocuments(accepted);
        const byId = new Map(outlines.map((outline) => [outline.documentId, outline]));

        setDocuments((current) => {
          const have = new Set(current.map((document) => `${document.filename}:${document.size}`));
          const incoming = accepted.filter((document) => !have.has(`${document.filename}:${document.size}`));
          return [...current, ...incoming.map((document) => ({ ...document, outline: byId.get(document.documentId) }))];
        });

        const withoutStructure = outlines.filter((outline) => outline.headingCount === 0 && outline.contentControlCount === 0);
        if (withoutStructure.length) {
          message.info(
            `No heading sections were found in ${withoutStructure.map((outline) => outline.filename).join(", ")}. The extracted text and tables are in Available content — use + to add them to a section.`
          );
        }
      } catch (error) {
        message.error(await readErrorMessage(error, "The documents could not be read."));
      } finally {
        setAnalyzing(false);
      }
    },
    [maxDocuments]
  );

  const handleRemoveDocument = useCallback((documentId) => {
    setDocuments((current) => {
      const remaining = current.filter((document) => document.documentId !== documentId);
      const remainingIds = remaining.map((document) => document.documentId);
      setSections((currentSections) => structure.pruneMissingDocuments(currentSections, remainingIds));
      return remaining;
    });
  }, []);

  // ----------------------------------------------------------------
  // Structure
  // ----------------------------------------------------------------

  const addSectionAt = useCallback((parentId) => {
    const section = structure.createSection("");
    setSections((current) => structure.addSection(current, parentId, section));
    setSelectedSectionId(section.id);
  }, []);

  const actions = useMemo(
    () => ({
      onSelect: setSelectedSectionId,
      onRename: (sectionId, title) => setSections((current) => structure.updateSection(current, sectionId, (section) => ({ ...section, title }))),
      onAddChild: addSectionAt,
      onMove: (sectionId, offset) => setSections((current) => structure.moveSection(current, sectionId, offset)),
      onRemove: (sectionId) => {
        setSections((current) => structure.removeSection(current, sectionId));
        setSelectedSectionId((current) => (current === sectionId ? null : current));
      },
      onRemoveBlock: (blockId) => setSections((current) => structure.removeBlock(current, blockId)),
      onMoveBlock: (blockId, offset) => setSections((current) => structure.moveBlock(current, blockId, offset)),
      onLoadOutline: () => {
        const outline = psmfOutline();
        setSections(outline);
        setSelectedSectionId(outline[0]?.id || null);
      }
    }),
    [addSectionAt]
  );

  const addSourceToSection = useCallback((source, sectionId) => {
    if (!sectionId) return;
    setSections((current) => structure.addBlock(current, sectionId, structure.createBlock(source)));
  }, []);

  const handleAddFromOutline = useCallback(
    (node) => {
      const source = {
        documentId: node.documentId,
        filename: node.filename,
        nodeId: node.id,
        label: node.title,
        type: node.type,
        level: node.level,
        blockCount: node.blockCount
      };

      if (selectedSectionId) {
        addSourceToSection(source, selectedSectionId);
        return;
      }

      // No section selected: make one and drop the content in, so documents
      // without a heading outline can still be assembled.
      const section = structure.createSection("");
      setSections((current) => structure.addBlock(structure.addSection(current, null, section), section.id, structure.createBlock(source)));
      setSelectedSectionId(section.id);
    },
    [addSourceToSection, selectedSectionId]
  );

  // ----------------------------------------------------------------
  // Drag and drop
  // ----------------------------------------------------------------

  // A short drag threshold keeps the buttons that live inside draggable rows
  // clickable, since a click no longer registers as the start of a drag.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = useCallback(
    ({ active, over }) => {
      if (!over) return;

      const activeData = active.data.current || {};
      const overData = over.data.current || {};

      const targetSectionId = overData.kind === "section" ? overData.sectionId : overData.kind === "block" ? structure.sectionHoldingBlock(sections, overData.blockId)?.id : null;

      if (!targetSectionId) return;

      if (activeData.kind === "source") {
        const target = structure.findSection(sections, targetSectionId);
        const index = overData.kind === "block" ? target.blocks.findIndex((block) => block.id === overData.blockId) : null;
        setSections((current) => structure.addBlock(current, targetSectionId, structure.createBlock(activeData.source), index));
        setSelectedSectionId(targetSectionId);
        return;
      }

      if (activeData.kind === "block" && activeData.blockId !== overData.blockId) {
        const target = structure.findSection(sections, targetSectionId);
        const index = overData.kind === "block" ? target.blocks.findIndex((block) => block.id === overData.blockId) : null;
        setSections((current) => structure.relocateBlock(current, activeData.blockId, targetSectionId, index));
      }
    },
    [sections]
  );

  // ----------------------------------------------------------------
  // Generation
  // ----------------------------------------------------------------

  const blockTotal = useMemo(() => structure.countBlocks(sections), [sections]);
  const canGenerate = documents.length > 0 && sections.length > 0 && !generating;

  const handleGenerate = useCallback(async () => {
    setGenerating(true);
    setPreviewOpen(true);
    try {
      const document = await generateDocument({
        documents,
        structure: structure.toPayload(sections),
        cover
      });

      let pdfUrl = null;
      let pdfName = null;

      if (pdfAvailable) {
        try {
          const pdf = await convertToPdf(document.blob, document.filename);
          releasePreview();
          pdfUrl = window.URL.createObjectURL(pdf.blob);
          previewUrlRef.current = pdfUrl;
          pdfName = pdf.filename;
        } catch (error) {
          message.warning(await readErrorMessage(error, "The PDF preview could not be produced."));
        }
      }

      setResult({
        docxBlob: document.blob,
        docxName: document.filename,
        warnings: document.warnings,
        pdfUrl,
        pdfName
      });
    } catch (error) {
      message.error(await readErrorMessage(error, "The document could not be generated."));
      // With no earlier document to fall back on, the pop-up would only be empty.
      if (!result) setPreviewOpen(false);
    } finally {
      setGenerating(false);
    }
  }, [cover, documents, pdfAvailable, releasePreview, result, sections]);

  const handleDownloadDocx = useCallback(() => {
    if (result) downloadBlob(result.docxBlob, result.docxName);
  }, [result]);

  const handleDownloadPdf = useCallback(async () => {
    if (!result?.pdfUrl) return;
    const response = await fetch(result.pdfUrl);
    downloadBlob(await response.blob(), result.pdfName || "document.pdf");
  }, [result]);

  // ----------------------------------------------------------------

  const selectedSection = selectedSectionId ? structure.findSection(sections, selectedSectionId) : null;

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <div className={styles.workspace}>
        <div className={styles.columnLeft}>
          <SourcePanel documents={documents} analyzing={analyzing} maxDocuments={maxDocuments} onAdd={handleAddDocuments} onRemove={handleRemoveDocument} />
        </div>

        <div className={styles.columnMiddle}>
          <Card
            size="small"
            title="Available content"
            className={styles.panel}
            styles={{ body: { display: "flex", flexDirection: "column", overflow: "hidden", flex: 1 } }}
            extra={
              selectedSection ? (
                <Tag color="geekblue" className={styles.targetTag}>
                  adding to {selectedSection.title || "untitled section"}
                </Tag>
              ) : documents.length > 0 ? (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  + creates a section
                </Text>
              ) : null
            }
          >
            <div className={styles.scrollArea}>
              <OutlineTree
                documents={documents}
                addHint={selectedSection ? `Add to ${selectedSection.title || "untitled section"}` : "Create a section and add this content"}
                onAdd={handleAddFromOutline}
              />
            </div>
          </Card>
        </div>

        <div className={styles.columnRight}>
          <Card
            size="small"
            title="Document structure"
            className={styles.panel}
            styles={{ body: { display: "flex", flexDirection: "column", gap: 10, overflow: "hidden", flex: 1 } }}
            extra={
              <GenerateControls
                result={result}
                generating={generating}
                canGenerate={canGenerate}
                onGenerate={handleGenerate}
                onDownloadDocx={handleDownloadDocx}
                onDownloadPdf={handleDownloadPdf}
                onPreview={() => setPreviewOpen(true)}
              />
            }
          >
            <Collapse
              size="small"
              ghost
              items={[
                {
                  key: "details",
                  label: <Text style={{ fontSize: 12 }}>Document details</Text>,
                  children: (
                    <Space direction="vertical" size={6} style={{ width: "100%" }}>
                      <Input
                        size="small"
                        addonBefore="Title"
                        placeholder="Custom PSMF"
                        value={cover.documentTitle}
                        onChange={(event) => setCover((current) => ({ ...current, documentTitle: event.target.value }))}
                      />
                      <Input
                        size="small"
                        addonBefore="Type"
                        placeholder="CUSTOM PSMF"
                        value={cover.documentType}
                        onChange={(event) => setCover((current) => ({ ...current, documentType: event.target.value }))}
                      />
                      <Input
                        size="small"
                        addonBefore="Current data lock point"
                        value={cover.currentDataLockPoint}
                        onChange={(event) => setCover((current) => ({ ...current, currentDataLockPoint: event.target.value }))}
                      />
                      <Input
                        size="small"
                        addonBefore="Previous data lock point"
                        value={cover.previousDataLockPoint}
                        onChange={(event) => setCover((current) => ({ ...current, previousDataLockPoint: event.target.value }))}
                      />
                      <Checkbox checked={cover.includeToc} onChange={(event) => setCover((current) => ({ ...current, includeToc: event.target.checked }))}>
                        <Text style={{ fontSize: 12 }}>Include a table of contents</Text>
                      </Checkbox>
                      <Checkbox checked={cover.stripSourceNumbering} onChange={(event) => setCover((current) => ({ ...current, stripSourceNumbering: event.target.checked }))}>
                        <Text style={{ fontSize: 12 }}>Remove numbers typed into copied sub-headings</Text>
                      </Checkbox>
                    </Space>
                  )
                }
              ]}
            />

            <div className={styles.structureArea}>
              <div className={styles.subheading}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {structure.countSections(sections)} sections, {blockTotal} content blocks
                </Text>
                <Space size={8}>
                  {/* <Tooltip title="Load the standard PSMF section outline">
                    <Button size="small" icon={<ProfileOutlined />} onClick={actions.onLoadOutline}>
                      PSMF outline
                    </Button>
                  </Tooltip> */}
                  <Button size="small" icon={<PlusOutlined />} onClick={() => addSectionAt(null)}>
                    Section
                  </Button>
                </Space>
              </div>
              <div className={styles.scrollArea}>
                <StructurePanel sections={sections} selectedId={selectedSectionId} actions={actions} />
              </div>
            </div>
          </Card>
        </div>
      </div>

      <PreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        result={result}
        generating={generating}
        canGenerate={canGenerate}
        pdfAvailable={pdfAvailable}
        onGenerate={handleGenerate}
        onDownloadDocx={handleDownloadDocx}
        onDownloadPdf={handleDownloadPdf}
      />
    </DndContext>
  );
};

export default DocumentBuilder;
